import { BaseApi } from "@/services/BaseApi";
import { ApiResult } from "@/services/ApiResult";
import * as ApiInterfaces from "@/models/Interfaces";
import { GetProductPricesResponse, GetProductSlotsResponse, IdName, PreloadPricesAndSlotsResponse, ProductListItem, Slot } from "@/models/Product";
import { Customer, Order, OrderPayment, BusinessClient, OrderFilter, Budget } from "@/models/Order";
import { OrderStatus, PrintSize } from "@/models/Enums";
import { PelecardInitResponse } from "@/models/Interfaces";
import { CalendarSlot } from "@/models/Calendar";
import { CloseShiftResponseModel, ShiftBalanceModel, ShiftListFilter, ShiftListResponseModel, ShiftResponseModel } from "@/models/Shift";
import qs from "qs";
import moment from "moment";
import { VIRTUAL_SLOT_ID } from '@/models/Constants';

export function getTimezone() {
  return -new Date().getTimezoneOffset();
}

class Api extends BaseApi {
  public async deleteCalendarSlot(slotId: number) {
    return this.delete("api/calendar/DeleteCalendarSlot?slotId=" + slotId);
  }
  public async login(Username: string, Password: string, CashierGuid: string, Lang = ""): Promise<ApiResult<ApiInterfaces.LoginResponse>> {
    return this.post<ApiInterfaces.LoginResponse>("api/login/index", JSON.stringify({ Username, Password, CashierGuid, Lang }));
  }
  public async logout(): Promise<ApiResult<object>> {
    return this.get("api/login/logout");
  }
  public async init(): Promise<ApiResult<ApiInterfaces.InitResponse>> {
    return this.get("api/RefData/init");
  }
  public async changeLanguage(lang: string): Promise<ApiResult<ApiInterfaces.ChangeLanguage>> {
    return this.post<ApiInterfaces.ChangeLanguage>("api/login/changelanguage", JSON.stringify({ lang: lang }));
  }
  public async closeApplication() {
    return this.get("api/application/close");
  }
  public async getRefProducts(): Promise<ApiResult<ProductListItem[]>> {
    return this.get<ProductListItem[]>("api/product/getProducts");
  }
  public async getProductSlots(
    productId: number,
    priceTypeId: number[],
    fromDate: string,
    currencyId: number
  ): Promise<ApiResult<GetProductSlotsResponse>> {
    return this.get<GetProductSlotsResponse>("api/product/getProductSlots", 
      { 
        params: { productId, priceTypeId, date: fromDate, currencyId },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }), 
    });
  }
  public async getProductPrices(productId: number, priceTypeId: number, fromDate: string, businessClientId?: number|null): Promise<ApiResult<GetProductPricesResponse>> {
    return this.get<GetProductPricesResponse>("api/product/getProductPrices", { params: { productId, priceTypeId, date: fromDate, businessClientId } });
  }
  public async preloadPricesAndSlots(date: string): Promise<ApiResult<PreloadPricesAndSlotsResponse>> {
    return this.get<PreloadPricesAndSlotsResponse>("api/product/preloadpricesandslots", { params: { date } });
  }
  public async loadPricesByBusinessClient(date: string, businessClientId: number|null): Promise<ApiResult<GetProductPricesResponse>> {
    return this.get<GetProductPricesResponse>("api/product/GetPriceListByBusinessClient", { params: { date, businessClientId } });
  }
  public async getCalendarAvailability(from: string, to: string, refresh = false): Promise<ApiResult<ApiInterfaces.CalendarAvailabilityResponse>> {
    return this.get<ApiInterfaces.CalendarAvailabilityResponse>("api/RefData/GetCashierAvailability", {
      params: {
        from,
        to,
        refresh
      },
    });
  }
  public async CalendarGetDateTime(): Promise<ApiResult<string>> {
    return this.get<string>("api/calendar/CalendarGetDateTime");
  }
  public async GetProductPriceStartEndDate(priceId: number): Promise<ApiResult<ApiInterfaces.GetProductPriceStartEndDate>> {
    return this.get<ApiInterfaces.GetProductPriceStartEndDate>("api/product/getproductpricestartenddate", {
      params: {
        priceId,
      },
    });
  }
  public async SaveOrder(order: Order, budgets: Budget[] | null): Promise<ApiResult<ApiInterfaces.SaveOrderResponse>> {
    return this.post<ApiInterfaces.SaveOrderResponse>("api/order/saveOrder", JSON.stringify({ order, budgets }));
  }
  public async ValidateOrder(order: Order, budgets: Budget[] | null): Promise<ApiResult<ApiInterfaces.SaveOrderResponse>> {
    return this.post<ApiInterfaces.SaveOrderResponse>("api/order/validateOrder", JSON.stringify({ order, budgets }));
  }
  public async ClearOrder(order: Order, budgets: Budget[] | null): Promise<ApiResult<ApiInterfaces.SaveOrderResponse>> {
    return this.post<ApiInterfaces.SaveOrderResponse>("api/order/ClearOrder", JSON.stringify({ ...order, budgets }));
  }
  public async SetOrderStatus(orderId: number | null, status: OrderStatus, setZero = false): Promise<ApiResult<ApiInterfaces.ErrorObject>> {
    return this.put<ApiInterfaces.ErrorObject>("api/order/setOrderStatus", JSON.stringify({ orderId, status, setZero }));
  }
  public async SaveOrderWithCustomer(order: Order): Promise<ApiResult<ApiInterfaces.SaveOrderResponse>> {
    return this.post<ApiInterfaces.SaveOrderResponse>("api/order/saveOrderWithCustomer", JSON.stringify(order));
  }
  public async SaveCustomerDetails(orderId, request: Customer): Promise<ApiResult<object>> {
    return this.post<object>("api/order/SaveCustomerDetails", JSON.stringify({ orderId, customer: request }));
  }
  public async ProceedPayments(request: OrderPayment): Promise<ApiResult<ApiInterfaces.OrderPaymentResponse>> {
    return this.post<ApiInterfaces.OrderPaymentResponse>("api/order/ProceedPayments", JSON.stringify({ payment: request }));
  }
  public async SearchReservedOrders(filter: OrderFilter, getOld = false): Promise<ApiResult<ApiInterfaces.SearchReservedOrdersResponse>> {
    const orderNumber = filter.orderNumber && Number(filter.orderNumber);
    return this.post("api/order/search", JSON.stringify({
      OrderNumber: !orderNumber ? null : orderNumber,
      CustomerName: filter.customerName,
      Pnr: filter.pnr,
      Mobile: filter.mobile,
      BusinessClientId: filter.businessClientId,
      PageIndex: filter.pageIndex,
      GetOld: getOld,
      Keywords: filter.keywords,
      GroupName: filter.groupName,
      ArrivalDateFrom: filter.arrivalDateFrom ? moment(filter.arrivalDateFrom).format('YYYY-MM-DDT00:00:00') : null,
      ArrivalDateTo: filter.arrivalDateTo ? moment(filter.arrivalDateTo).format('YYYY-MM-DDT23:59:00') : null,
      ProductId: filter.productId
    }));
  }
  public async SearchReservedOrdersBy(filter: OrderFilter, getOld = false): Promise<ApiResult<ApiInterfaces.SearchReservedOrdersResponse>> {
    return this.post("api/order/search-by", JSON.stringify({
      SearchValue: filter.searchValue,
      SearchType: filter.searchType,
    }));
  }

  public async GetOrderDetails(orderId: number): Promise<ApiResult<ApiInterfaces.OrderDetailsResponse>> {
    return this.get("api/order/GetOrderDetails", { params: { orderId } });
  }
  public async GetOrderTicketPdfUrl(orderId: number): Promise<ApiResult<ApiInterfaces.PrintTicketResponse>> {
    return this.post("api/order/GetTicketPdfUrl", JSON.stringify({ orderId }));
  }
  public async PelecardInit(
    amount: number,
    currencyId: number,
    creditCardPaymentType: number,
    payments: number
  ): Promise<ApiResult<PelecardInitResponse>> {
    return this.get("api/pelecard/init", { params: { amount, currencyId, creditCardPaymentType, payments } });
  }
  public async ClubVoucherList(): Promise<ApiResult<BusinessClient[]>> {
    return this.get("api/order/ClubVoucherList");
  }
  public async MarkReadCashierMessage(messageId: number): Promise<ApiResult<ApiInterfaces.MarkReadCashierMessageResponse>> {
    return this.post<ApiInterfaces.MarkReadCashierMessageResponse>("api/RefData/MarkReadCashierMessage", JSON.stringify({ messageId }));
  }
  public async SearchCoupons(orderNumber, clubId): Promise<ApiResult<ApiInterfaces.SearchCouponResponse>> {
    return this.get("api/coupons/search", { params: { orderNumber, clubId } });
  }
  public async saveCalendarEditLimitQty(
    slot: CalendarSlot,
    disabledDate: string,
    ignoreWarning = false,
    ignoreStopSaleWarning = false
  ): Promise<ApiResult<ApiInterfaces.CalendarSlotSaveResponse>> {
    return this.post<ApiInterfaces.CalendarSlotSaveResponse>('api/Calendar/CalendarEditLimitQty', {
      slot,
      disabledDate: disabledDate || null,
      ignoreStopSaleWarning,
      ignoreWarning
    });
  }
  public async saveCalendarSlot(
    slot: CalendarSlot,
    disabledDate: string,
    ignoreWarning = false,
    ignoreStopSaleWarning = false
  ): Promise<ApiResult<ApiInterfaces.CalendarSlotSaveResponse>> {
    return this.post<ApiInterfaces.CalendarSlotSaveResponse>('api/Calendar/SaveCalendarSlot', {
      slot,
      disabledDate: disabledDate || null,
      ignoreStopSaleWarning,
      ignoreWarning
    });
  }
  public async waitExportTask(taskId: number) {
    return this.get<ApiInterfaces.AwaitTaskResponse>('api/order/WaitTicketUrl', { params: { id: taskId } });
  }
  public async CreateOrderTicketJob(orderId: number, printingType: number, printSize): Promise<ApiResult<ApiInterfaces.CreateOrderTicketJobResponse>> {
    return this.post<ApiInterfaces.CreateOrderTicketJobResponse>("api/order/CreateOrderTicketTask", JSON.stringify({ orderId, printingType, printSize }));
  }
  public async PrintOrderTicketByUrl(pdfUrl: string): Promise<ApiResult<ApiInterfaces.CreateOrderTicketJobResponse>> {
    return this.post<ApiInterfaces.CreateOrderTicketJobResponse>("api/order/PrintOrderTicketByUrl", JSON.stringify({ pdfUrl }));
  }
  public async CreateInvoice(orderId: number): Promise<ApiResult<ApiInterfaces.CreateInvoiceResponse>> {
    return this.post<ApiInterfaces.CreateInvoiceResponse>("api/order/CreateInvoice", JSON.stringify({ orderId }));
  }
  public async PrintReceipt(PdfUrl: string): Promise<ApiResult<object>> {
    return this.post("api/order/PrintReceipt", JSON.stringify({ PdfUrl }));
  }
  public async getSettings() {
    return this.get<ApiInterfaces.Settings>('api/application/getsettings');
  }
  public async setSettings(setting: ApiInterfaces.Settings) {
    return this.post('api/application/setsettings', setting);
  }
  public async getPrinters() {
    return this.get<string[]>('api/application/printers');
  }
  public async GetOrderLinkUrl(orderId: number) {
    return this.get<string>("api/order/GenerateCustomerAppOrderLink", {
      params: {
        orderId,
      },
    });
  }
  public async openShift(paperSize: PrintSize, balances: ShiftBalanceModel[], checkbalances: ShiftBalanceModel[]){
      return this.post<ShiftResponseModel>('api/shift/OpenShift', JSON.stringify({
          shiftBalancesCash: balances,
          shiftBalancesCheck: checkbalances,
          paperSize
      }));
  }
  public async getCloseShift(){
      return this.get<CloseShiftResponseModel>('api/shift/CloseShift');
  }
  public async closeShift(paperSize: PrintSize, managerCode: string, balanceDescription: string){
      return this.post<ShiftResponseModel>('api/shift/CloseShift', JSON.stringify({
          managerCode,
          balanceDescription,
          paperSize
      }));
  }
  public async searchShiftList(filter: ShiftListFilter){
      return this.post<ShiftListResponseModel[]>("api/shift/SearchShiftList", {
          keywords: filter.keywords,
          pageIndex: filter.pageIndex,
          startDate: filter.startDate,
          endDate: filter.endDate
      });
  }
  public async validateOpenShift(){
      return this.get("api/shift/OpenShiftVaidation");
  }
  public async getAllActiveProducts(){
      return this.get<IdName[]>("api/order/GetAllActiveProducts");
  }
  public async generateShiftReport(paperSize: PrintSize | null, shiftId: string, isOpenShift: boolean){
    return this.post<number>('api/shift/GenerateShiftReport', {
        shiftId,
        isOpenShift,
        paperSize
    });
  }
  public async getAvailableSlotForTodayByProductId(productId: number, priceTypeId: number){
    return this.get<Slot>("api/product/GetAvailableSlotForTodayByProductId", { params: { productId, priceTypeId } });
  }
  public async openMoneyDrawer(){
    return this.get<number>('api/shift/OpenMoneyDrawer');
  }
}

export const api = new Api();